import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { TFunction } from 'i18next';
import {
  ContentHubSpecialOffersProps,
  ContentHubSpecialOffersRendererFields,
  ContentHubSpecialOffersRendererParams,
  IContentHubSpecialOffersSlideFields,
  TAsset,
} from '../types/specialOffers.types';
import { ImageField, LinkField, StringField, TLinkField, TRendition } from '../../../types/types';
import { BACKGROUND_COLOR_MAP } from '../../../constants/background-color.constants';
import { appendRootToRichTextInternalLinks } from '../../../utils/appendRootToRichTextInternalLinks.utils';
import { SP_QUERY_STRINGS } from '../../../constants/special-offers-page.constants';

export class ContentHubSpecialOffersSlideModel {
  private readonly card: IContentHubSpecialOffersSlideFields;
  private readonly fields: null | ContentHubSpecialOffersRendererFields;
  private readonly itemIndex: number;
  private readonly isEE: boolean;
  private readonly t?: TFunction;
  private readonly specialOfferMainPageLink: string;

  constructor(Card: IContentHubSpecialOffersSlideFields, index, isEE, t?: TFunction, specialOfferMainPageLink?: string) {
    this.card = Card || null;
    this.itemIndex = index;
    this.isEE = isEE;
    this.t = t;
    this.specialOfferMainPageLink = specialOfferMainPageLink || '';
  }

  get id(): string {
    return this.card?.id ?? '';
  }

  get titleUrl(): string {
    return this.card?.titleUrl || '';
  }

  get title(): StringField {
    return { value: this.card?.formattedTitle };
  }

  get shortDescription(): StringField {
    return { value: this.card?.shortDescription};
  }

  get description(): StringField {
    return { value: this.card?.description };
  }

  get type(): StringField {
    const defaultValue = { value: 'offerCardRegular' };
    if (this.itemIndex === 0) defaultValue.value = 'offerCardWide';
    return defaultValue;
  }

  get disclaimer(): string {
    return this.card?.disclaimer;
  }

  get expiration(): StringField {
    return { value: this.card?.expiration };
  }

  get badge(): string {
    return this.card?.badge;
  }

  get expirationLabel(): StringField {
    return { value: this.card?.expirationLabel };
  }

  get image(): ImageField {
    return {
      value: {
        src: this.card?.assets?.[0]?.full,
        alt: this.card?.assets?.[0]?.alt,
      },
    };
  }

  get asset(): TAsset | undefined {
    return this.card?.assets?.[0];
  }

  get buttonPrimary(): LinkField {
    if (!this.titleUrl || !this.specialOfferMainPageLink) return { value: { href: '', title: '' } };
    const title = this.t?.('cnhi-special-offer-learn-more');
    return {
      value: {
        href: `${this.specialOfferMainPageLink}/${this.titleUrl}`,
        title,
        text: title,
      },
    };
  }

  get buttonSecondary(): LinkField {
    return {
      value: {
        href: this.card?.button2?.url,
        title: this.card?.button2?.label,
      },
    };
  }

  get hasWideCardStyle(): boolean {
    return this.card?.cardStyles === 'Wide';
  }

  get desktopRendition(): TRendition | undefined {
    return this.hasWideCardStyle ? this.asset?.rendition_desktop_size1000 : this.asset?.rendition_desktop_size500;
  }

  get mobileRendition(): TRendition | undefined {
    return this.asset?.rendition_mobile_size300;
  }

  get secondaryButtonExistsOrIsEE(): boolean {
    return !!((this.card?.button2?.url && this.card?.button2?.label) || this.isEE);
  }
}

export class ContentHubSpecialOffersModel {
  private readonly params: null | ContentHubSpecialOffersRendererParams;
  private readonly fields: null | ContentHubSpecialOffersRendererFields;
  private readonly rendering?: ComponentRendering;
  private readonly isEE: boolean;
  private readonly t?: TFunction;
  private readonly specialOfferMainPageLink: string;

  constructor(props: ContentHubSpecialOffersProps, isEE: boolean, t?: TFunction, specialOfferMainPageLink?: string) {
    this.params = props?.params || null;
    this.fields = props?.fields || null;
    this.rendering = props?.rendering;
    this.isEE = isEE;
    this.t = t;
    this.specialOfferMainPageLink = specialOfferMainPageLink || '';
  }

  get backgroundColor(): string | undefined {
    return BACKGROUND_COLOR_MAP.get(this.params?.backgroundColor || '');
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get eyebrow(): StringField {
    return { value: this.fields?.apiData?.eyebrow };
  }

  get title(): StringField {
    return { value: this.fields?.apiData?.title };
  }

  get description(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.fields?.apiData?.description) };
  }

  getLink(lineId: string): TLinkField | undefined {
    if (!lineId || !this.specialOfferMainPageLink) return;
    const title = this.t?.('cnhi-special-offer-read-more') || '';
    return {
      value: {
        href: `${this.specialOfferMainPageLink}?${SP_QUERY_STRINGS.LINE}=${lineId}`,
        title,
        text: title,
      },
    };
  }

  get items() {
    const todayDate = new Date();
    const items: IContentHubSpecialOffersSlideFields[] = this.fields?.apiData?.items || [];
    const cardStyles = (this.params?.cardStyles && this.params?.cardStyles.split('|')) || [];
    const validSpecialOffers = items.filter((offer, index) => {
      offer['cardStyles'] = cardStyles[index];
      const expiryDate = new Date(offer.expiration || '');
      return expiryDate.setHours(0, 0, 0, 0) >= todayDate.setHours(0, 0, 0, 0);
    });
    const defaultValue =
      validSpecialOffers &&
      validSpecialOffers.map((Card, index) => {
        return new ContentHubSpecialOffersSlideModel(Card, index, this.isEE, this.t, this.specialOfferMainPageLink);
      });
    if (this.fields && this.fields['Cards']) {
      return this.fields['Cards'].map((Card, index) => {
        return new ContentHubSpecialOffersSlideModel(Card, index, this.isEE, this.t, this.specialOfferMainPageLink);
      });
    }
    return defaultValue;
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get contextItemUrl(): string {
    return this.fields?.itemData?.contextItemUrl || '';
  }

  get modelDependencies(): string[] {
    return this.contextItemUrl ? [this.contextItemUrl] : [];
  }
}
